import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { DateTime } from 'luxon';
import React from 'react';

import { stripHtmlTags } from '@poseidon/shared-utils/html';

import { cn } from '../cn';
import Button from '../Input2/Button';
import { DocumentCard, FileAttachment, ImageViewer, MusicPlayer, VideoPlayer } from '../MediaPlayer';
import { Arrows, Editor, General, Time, Users } from '../StrokeIcons';
import { Tooltip } from '../Tooltip';

// ─────────────────────────────────────────────────────────────────────────────
// Utility helpers (inlined to avoid external dependency)
// ─────────────────────────────────────────────────────────────────────────────

const isNotNull = <T,>(arg: T | undefined | null): arg is T => {
  return !(arg === null || arg === undefined);
};

const isNotEmpty = (arg: string | undefined | null): arg is string => {
  return isNotNull(arg) && arg.length > 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Types (flat primitives, no server imports)
// ─────────────────────────────────────────────────────────────────────────────

export type HistoryStepType =
  | 'unknown'
  | 'mail'
  | 'inmail'
  | 'message'
  | 'mailReplied'
  | 'startedCadence'
  | 'campaignDelete'
  | 'document'
  | 'error'
  | 'visit'
  | 'call'
  | 'comment'
  | 'connect'
  | 'connectAccept'
  | 'meetingBooked'
  | 'interestDetected'
  | 'stageChanged'
  | 'reminder'
  | 'note'
  | 'follow'
  | 'owner'
  | 'postsLike'
  | 'declinedInmail'
  | 'contactUnavailable'
  | 'contactAvailable'
  | 'cadenceStateChanged';

export type InteractionAction =
  | 'connection_sent'
  | 'connection_accepted'
  | 'message_sent'
  | 'message_received'
  | 'inmail_sent'
  | 'inmail_received'
  | 'inmail_declined'
  | 'email_sent'
  | 'email_received'
  | 'phone_call_sent'
  | 'phone_received'
  | 'cadence_start'
  | 'campaign_start'
  | 'document_viewed'
  | 'comment_on_post'
  | 'stage_changed'
  | 'visit'
  | 'follow'
  | 'like_posts'
  | 'reminder_completed'
  | 'campaign_delete'
  | 'step_failed'
  | 'step_skipped'
  | 'step_canceled'
  | 'meeting_booked'
  | 'interest_detected'
  | 'contact_unavailable'
  | 'contact_available'
  | 'cadence_state_changed';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getInteractionStepType(action: InteractionAction): HistoryStepType {
  switch (action) {
    case 'connection_accepted':
      return 'connectAccept';
    case 'connection_sent':
      return 'connect';
    case 'email_sent':
      return 'mail';
    case 'inmail_sent':
    case 'inmail_received':
      return 'inmail';
    case 'message_sent':
    case 'message_received':
      return 'message';
    case 'email_received':
      return 'mailReplied';
    case 'cadence_start':
    case 'campaign_start':
      return 'startedCadence';
    case 'document_viewed':
      return 'document';
    case 'phone_received':
    case 'phone_call_sent':
      return 'call';
    case 'comment_on_post':
      return 'comment';
    case 'inmail_declined':
      return 'declinedInmail';
    case 'stage_changed':
      return 'stageChanged';
    case 'follow':
      return 'follow';
    case 'like_posts':
      return 'postsLike';
    case 'reminder_completed':
      return 'reminder';
    case 'visit':
      return 'visit';
    case 'campaign_delete':
      return 'campaignDelete';
    case 'meeting_booked':
      return 'meetingBooked';
    case 'interest_detected':
      return 'interestDetected';
    case 'step_failed':
    case 'step_skipped':
    case 'step_canceled':
      return 'error';
    case 'contact_unavailable':
      return 'contactUnavailable';
    case 'contact_available':
      return 'contactAvailable';
    case 'cadence_state_changed':
      return 'cadenceStateChanged';
    default:
      return 'unknown';
  }
}

function getCapitalizedAllString(string: string) {
  const prettyStringArr = string.toLowerCase().split('_');
  const prettyString = prettyStringArr.map((str) => str.charAt(0).toUpperCase() + str.slice(1)).join(' ');
  return prettyString;
}

function contactNameFlat(contact: { firstName?: string | null; lastName?: string | null }): string | undefined {
  const parts = [contact.firstName, contact.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * Clean email content for display - removes signatures, quoted replies, calendar invite cruft, etc.
 */
function cleanEmailContent(html: string): string {
  let text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  const quotedReplyPatterns = [
    /On\s+\w+,?\s+\w+\s+\d{1,2},?\s+\d{4}.*?wrote:[\s\S]*/i,
    /On\s+\d{1,2}\/\d{1,2}\/\d{2,4}.*?wrote:[\s\S]*/i,
    /-{3,}\s*Original Message\s*-{3,}[\s\S]*/i,
    /From:.*?Sent:.*?To:.*?Subject:[\s\S]*/is,
    /_{3,}\s*[\s\S]*$/,
  ];

  for (const pattern of quotedReplyPatterns) {
    text = text.replace(pattern, '');
  }

  const signaturePatterns = [
    /\n\s*(Best|Thanks|Thank you|Regards|Cheers|Sincerely|Warm regards|Kind regards|Best regards),?\s*\n[A-Z][a-z]+[\s\S]{0,500}$/i,
    /\n\s*[A-Z][a-z]+\s+[A-Z][a-z]+,?\s*(CFA|CFP|CPA|MBA|PhD|JD|MD|CEPA|CBDA)®?[\s\S]{0,500}$/,
    /\n.*?\+?1?\s*[-.(]?\d{3}[-.)]\s*\d{3}[-.]?\d{4}[\s\S]{0,300}$/,
    /\n.*?(linkedin\.com|schedule.*meeting|calendly\.com)[\s\S]{0,200}$/i,
  ];

  for (const pattern of signaturePatterns) {
    text = text.replace(pattern, '');
  }

  text = text
    .replace(/Join Zoom Meeting[\s\S]*?passcode:\s*\d+/gi, '')
    .replace(/Meeting ID:\s*\d[\d\s]+/gi, '')
    .replace(/Passcode:\s*\d+/gi, '')
    .replace(/Join by phone[\s\S]*?(\n\n|$)/gi, '')
    .replace(/https?:\/\/[^\s]*?(zoom\.us|teams\.microsoft|meet\.google)[^\s]*/gi, '[Meeting Link]');

  text = text
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/^\s+|\s+$/gm, '')
    .trim();

  return text;
}

function formatViewDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  return remainingMin > 0 ? `${hours}h ${remainingMin}m` : `${hours}h`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Props (flat primitives)
// ─────────────────────────────────────────────────────────────────────────────

export interface InteractionStepProps {
  // Current user (for determining "your" vs "their")
  currentUserId: string;

  // Contact info (flat)
  contactFirstName?: string | null;
  contactLastName?: string | null;

  // Interaction data (flat)
  interactionId: string;
  interactionUserId: string;
  interactionAction: InteractionAction;
  interactionDate: Date;
  interactionUpdatedOn: Date;
  interactionSubject?: string | null;
  interactionBody?: string | null;
  interactionDataUrl?: string | null;
  interactionDataUrlType?: 'audio' | 'video' | 'img' | 'file' | null;

  // Interaction user info (flat)
  actorName?: string | null;
  actorEmail: string;

  // Document view info (flat, optional)
  documentId?: string | null;
  documentHref?: string;
  documentTitle?: string | null;
  documentDescription?: string | null;
  documentOgImageUrl?: string | null;
  documentCity?: string | null;
  documentCountry?: string | null;
  documentBrowserName?: string | null;
  documentOs?: string | null;

  // Cadence info (flat)
  cadenceName?: string | null;
  cadenceId?: string | null;

  // Callbacks for cadence click
  onClickCadence?: (cadenceId: string) => void;

  // Previous interaction date (for day grouping)
  prevInteractionDate?: Date | null;

  // UI flags
  isLast?: boolean;

  // Callbacks
  onClickToRespond?: (type: InteractionAction, eventId?: string) => void;
  onMarkMeetingBooked: (interactionPublicId: string) => void;
  isMarkingMeetingBooked?: boolean;

  // Retry attachment callbacks
  onRetryAttachment?: (interactionId: string) => void;
  retryState?: 'idle' | 'pending' | 'success' | 'error';
  retryError?: string | null;

  // Transcription data
  transcription?: string | null;
  transcriptionModel?: string | null;
  transcriptionStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
  transcriptionError?: string | null;
  onRequestTranscription?: (interactionId: string) => void;
  transcriptionRequestState?: 'idle' | 'pending' | 'success' | 'error';

  // Meeting booked from this interaction
  meetingBookedFromThis?: {
    id: string;
    source: 'auto' | 'manual';
  } | null;
}

export function InteractionStep({
  currentUserId,
  contactFirstName,
  contactLastName,
  interactionId,
  interactionUserId,
  interactionAction,
  interactionDate,
  interactionUpdatedOn,
  interactionSubject,
  interactionBody,
  interactionDataUrl,
  interactionDataUrlType,
  actorName,
  actorEmail,
  documentId,
  documentHref,
  documentTitle,
  documentDescription,
  documentOgImageUrl,
  documentCity,
  documentCountry,
  documentBrowserName,
  documentOs,
  cadenceName,
  cadenceId,
  onClickCadence,
  prevInteractionDate,
  isLast,
  onClickToRespond,
  onMarkMeetingBooked,
  isMarkingMeetingBooked,
  onRetryAttachment,
  retryState = 'idle',
  retryError,
  transcription,
  transcriptionModel,
  transcriptionStatus,
  transcriptionError,
  onRequestTranscription,
  transcriptionRequestState = 'idle',
  meetingBookedFromThis,
}: InteractionStepProps) {
  const nameFull = contactNameFlat({ firstName: contactFirstName, lastName: contactLastName });
  const displayActorName = actorName ?? actorEmail;
  const isCurrentUser = currentUserId === interactionUserId;

  const responseTypes: InteractionAction[] = [
    'connection_accepted',
    'message_received',
    'inmail_received',
    'inmail_declined',
    'email_received',
    'document_viewed',
  ];
  const isResponse = responseTypes.includes(interactionAction);
  const showRespondIcon = ['message_received', 'inmail_received', 'email_received', 'document_viewed'].includes(
    interactionAction,
  );

  const [menuOpen, setMenuOpen] = React.useState(false);
  const { x, y, strategy, refs, context } = useFloating({
    open: menuOpen,
    onOpenChange: setMenuOpen,
    placement: isResponse ? 'bottom-end' : 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const stepType = getInteractionStepType(interactionAction);

  const stepDate = DateTime.fromJSDate(interactionDate);
  const now = DateTime.local();
  const isToday = stepDate.hasSame(now, 'day');
  const isYesterday = stepDate.hasSame(now.minus({ days: 1 }), 'day');
  const isSameYear = stepDate.hasSame(now, 'year');

  const formattedStepDate = isToday
    ? 'Today'
    : isYesterday
      ? 'Yesterday'
      : isSameYear
        ? stepDate.toFormat('LLL d')
        : stepDate.toFormat('LLL d, yyyy');
  const tooltipDate = stepDate.toFormat('ccc LLL d, yyyy h:mm a');

  const isSameDay = prevInteractionDate
    ? stepDate.hasSame(DateTime.fromJSDate(prevInteractionDate), 'day')
    : false;

  const minutesSincePrev = prevInteractionDate
    ? Math.abs(stepDate.diff(DateTime.fromJSDate(prevInteractionDate), 'minutes').minutes)
    : Infinity;
  const showTime = !isSameDay || minutesSincePrev >= 60 || isLast;
  const formattedTime = stepDate.toFormat('h:mm a').toLowerCase();

  const classList = cn('flex gap-2 max-w-[80%]', isResponse ? 'self-start' : 'self-end');

  const onClickReply = () => {
    if (
      interactionAction === 'message_received' ||
      interactionAction === 'email_received' ||
      interactionAction === 'document_viewed'
    ) {
      onClickToRespond?.(interactionAction);
    }
  };

  const isEventType =
    stepType === 'connectAccept' ||
    stepType === 'meetingBooked' ||
    stepType === 'interestDetected' ||
    stepType === 'stageChanged' ||
    stepType === 'contactUnavailable' ||
    stepType === 'contactAvailable' ||
    stepType === 'reminder' ||
    stepType === 'cadenceStateChanged' ||
    stepType === 'startedCadence' ||
    stepType === 'campaignDelete';

  // Build document view data if present
  const hasDocumentView = documentId != null;
  const viewingDuration = hasDocumentView ? interactionUpdatedOn.getTime() - interactionDate.getTime() : undefined;

  return (
    <>
      {!isSameDay && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <span className="self-center text-[11px] font-medium text-gray-500 dark:text-gray-400 py-1.5">
            {formattedStepDate}
          </span>
        </Tooltip>
      )}
      {stepType === 'connectAccept' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-green-50 dark:bg-green-900/30 px-3 py-1.5 border border-green-200 dark:border-green-800">
              <span className="text-[13px] font-semibold text-green-700 dark:text-green-300 leading-tight tracking-tight">
                {nameFull
                  ? `${nameFull} accepted ${isCurrentUser ? 'your' : `${displayActorName}'s`} connection`
                  : 'Connection accepted'}
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              Sent by {displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'meetingBooked' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 border border-blue-200 dark:border-blue-800 flex items-center gap-1.5">
              <Time.Calendar className="size-3.5 text-blue-600 dark:text-blue-400" />
              <span className="text-[13px] font-semibold text-blue-700 dark:text-blue-300 leading-tight tracking-tight">
                Meeting booked
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {interactionBody === 'auto detected' ? 'Auto detected' : `Marked by ${displayActorName}`} ·{' '}
              {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'interestDetected' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 border border-blue-200 dark:border-blue-800 flex items-center gap-1.5">
              <General.Zap className="size-3.5 text-blue-600 dark:text-blue-400" />
              <span className="text-[13px] font-semibold text-blue-700 dark:text-blue-300 leading-tight tracking-tight">
                Interest detected
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {interactionBody === 'auto detected' ? 'Auto detected' : `Marked by ${displayActorName}`} ·{' '}
              {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'stageChanged' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-purple-50 dark:bg-purple-900/30 px-3 py-1.5 border border-purple-200 dark:border-purple-800 flex items-center gap-1.5">
              <Arrows.SwitchVertical02 className="size-3.5 text-purple-600 dark:text-purple-400" />
              <span className="text-[13px] font-semibold text-purple-700 dark:text-purple-300 leading-tight tracking-tight">
                {interactionSubject ? `Moved to ${interactionSubject}` : 'Stage changed'}
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {interactionBody ? `${interactionBody} · ` : ''}
              {displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'contactUnavailable' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-red-50 dark:bg-red-900/30 px-3 py-1.5 border border-red-200 dark:border-red-800 flex items-center gap-1.5">
              <General.XCircle className="size-3.5 text-red-600 dark:text-red-400" />
              <span className="text-[13px] font-semibold text-red-700 dark:text-red-300 leading-tight tracking-tight">
                Contact unavailable
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'contactAvailable' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-green-50 dark:bg-green-900/30 px-3 py-1.5 border border-green-200 dark:border-green-800 flex items-center gap-1.5">
              <General.CheckCircle className="size-3.5 text-green-600 dark:text-green-400" />
              <span className="text-[13px] font-semibold text-green-700 dark:text-green-300 leading-tight tracking-tight">
                Contact available
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'reminder' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 border border-amber-200 dark:border-amber-800 flex items-center gap-1.5">
              <Time.AlarmClock className="size-3.5 text-amber-600 dark:text-amber-400" />
              <span className="text-[13px] font-semibold text-amber-700 dark:text-amber-300 leading-tight tracking-tight">
                Reminder completed
              </span>
            </div>
            {interactionBody && (
              <span className="text-[12px] text-gray-600 dark:text-gray-300 text-center max-w-[300px]">
                {interactionBody}
              </span>
            )}
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'startedCadence' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1.5 border border-gray-200 dark:border-gray-700 flex items-center gap-1.5">
              <General.Zap className="size-3.5 text-gray-500 dark:text-gray-400" />
              <span className="text-[13px] font-semibold text-gray-600 dark:text-gray-300 leading-tight tracking-tight">
                Campaign started
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {cadenceName && cadenceId && onClickCadence ? (
                <>
                  <button
                    type="button"
                    onClick={() => onClickCadence(cadenceId)}
                    className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline cursor-pointer">
                    {cadenceName}
                  </button>
                  {' · '}
                </>
              ) : cadenceName ? `${cadenceName} · ` : ''}{displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'campaignDelete' && (
        <Tooltip label={tooltipDate} autoPlace={false}>
          <div className="self-center flex flex-col items-center gap-1 py-2">
            <div className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1.5 border border-gray-200 dark:border-gray-700 flex items-center gap-1.5">
              <Arrows.SwitchVertical02 className="size-3.5 text-gray-500 dark:text-gray-400" />
              <span className="text-[13px] font-semibold text-gray-600 dark:text-gray-300 leading-tight tracking-tight">
                Campaign deleted
              </span>
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {cadenceName && cadenceId && onClickCadence ? (
                <>
                  <button
                    type="button"
                    onClick={() => onClickCadence(cadenceId)}
                    className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline cursor-pointer">
                    {cadenceName}
                  </button>
                  {' · '}
                </>
              ) : cadenceName ? `${cadenceName} · ` : ''}{displayActorName} · {formattedStepDate}
            </span>
          </div>
        </Tooltip>
      )}
      {stepType === 'cadenceStateChanged' && (() => {
        // Parse body format: "previousState → newState" or "previousState → newState (reason)"
        const parts = interactionBody?.split(' → ') ?? [];
        let newStateRaw = parts[1] ?? interactionBody ?? 'unknown';

        // Extract reason if present (e.g., "finished (manual message queued)" → state="finished", reason="manual message queued")
        let reason: string | null = null;
        const reasonMatch = newStateRaw.match(/^(.+?)\s*\((.+)\)$/);
        if (reasonMatch && reasonMatch[1] && reasonMatch[2]) {
          newStateRaw = reasonMatch[1].trim();
          reason = reasonMatch[2].trim();
        }
        const newState = newStateRaw;

        // Defensive: hide no-op "transitions" where the state didn't actually change (e.g. an
        // `active → active` row re-asserted by a heal/reactivate job). The writer now skips these,
        // but this also suppresses any residual rows recorded before that guard shipped. Only hide
        // when both sides positively parsed and match — never hide a malformed/legacy row.
        const previousState = parts[0]?.trim();
        if (parts.length >= 2 && previousState != null && previousState === newState) {
          return null;
        }

        // Color based on state
        const stateColors: Record<string, { bg: string; border: string; text: string; icon: string }> = {
          active: { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-600 dark:text-blue-400' },
          'not-started': { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-600 dark:text-blue-400' },
          researching: { bg: 'bg-blue-50 dark:bg-blue-900/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-600 dark:text-blue-400' },
          paused: { bg: 'bg-yellow-50 dark:bg-yellow-900/30', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', icon: 'text-yellow-600 dark:text-yellow-400' },
          'pending-review': { bg: 'bg-yellow-50 dark:bg-yellow-900/30', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', icon: 'text-yellow-600 dark:text-yellow-400' },
          interrupted: { bg: 'bg-red-50 dark:bg-red-900/30', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300', icon: 'text-red-600 dark:text-red-400' },
          finished: { bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300', icon: 'text-green-600 dark:text-green-400' },
          responded: { bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300', icon: 'text-green-600 dark:text-green-400' },
          deleted: { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-700', text: 'text-gray-600 dark:text-gray-300', icon: 'text-gray-500 dark:text-gray-400' },
        };
        const defaultColors = { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-700', text: 'text-gray-600 dark:text-gray-300', icon: 'text-gray-500 dark:text-gray-400' };
        // Forced finish (has a reason) uses amber instead of green
        const forcedFinishColors = { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300', icon: 'text-amber-600 dark:text-amber-400' };
        const colors = (newState === 'finished' && reason) ? forcedFinishColors : (stateColors[newState] ?? defaultColors);

        return (
          <Tooltip label={tooltipDate} autoPlace={false}>
            <div className="self-center flex flex-col items-center gap-1 py-2">
              <div className={cn('rounded-full px-3 py-1.5 border flex items-center gap-1.5', colors.bg, colors.border)}>
                <Arrows.SwitchVertical02 className={cn('size-3.5', colors.icon)} />
                <span className={cn('text-[13px] font-semibold leading-tight tracking-tight', colors.text)}>
                  {newState === 'deleted' ? 'Campaign deleted' : `Campaign ${newState.replace('-', ' ')}`}
                </span>
              </div>
              {reason && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {reason}
                </span>
              )}
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {cadenceName && cadenceId && onClickCadence ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onClickCadence(cadenceId)}
                      className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline cursor-pointer">
                      {cadenceName}
                    </button>
                    {' · '}
                  </>
                ) : cadenceName ? `${cadenceName} · ` : ''}{displayActorName} · {formattedStepDate}
              </span>
            </div>
          </Tooltip>
        );
      })()}
      {!isEventType && (
        <div className={cn(classList, 'group flex items-start gap-1', isResponse ? 'flex-row-reverse' : 'flex-row')}>
          <button
            ref={refs.setReference}
            {...getReferenceProps()}
            className={cn(
              'mt-5 p-1 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity shrink-0',
              'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto',
            )}
          >
            <General.DotsVertical className="size-4 text-gray-500 dark:text-gray-400" />
          </button>
          <div className="flex flex-col gap-0.5 relative min-w-0">
            <span
              className={cn(
                'text-[10px] text-gray-400 dark:text-gray-500 px-1',
                isResponse ? 'text-left' : 'text-right',
              )}
            >
              {isResponse ? (nameFull ?? displayActorName) : displayActorName}
            </span>
            {menuOpen && (
              <FloatingPortal>
                <div
                  ref={refs.setFloating}
                  {...getFloatingProps()}
                  style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                  }}
                  className="z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[180px]"
                >
                  <button
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                    disabled={isMarkingMeetingBooked}
                    onClick={() => {
                      onMarkMeetingBooked(interactionId);
                      setMenuOpen(false);
                    }}
                  >
                    <Time.Calendar className="size-4" />
                    Mark meeting booked from this
                  </button>
                </div>
              </FloatingPortal>
            )}
            <StepContent
              interactionId={interactionId}
              dataUrl={interactionDataUrl}
              dataUrlType={interactionDataUrlType}
              contactName={nameFull}
              stepType={stepType}
              isResponse={isResponse}
              formattedDate={formattedStepDate}
              action={interactionAction}
              title={interactionSubject}
              body={interactionBody}
              cadenceName={cadenceName}
              cadenceId={cadenceId}
              onClickCadence={onClickCadence}
              documentId={documentId}
              documentHref={documentHref}
              documentTitle={documentTitle}
              documentDescription={documentDescription}
              documentOgImageUrl={documentOgImageUrl}
              documentCity={documentCity}
              documentCountry={documentCountry}
              documentBrowserName={documentBrowserName}
              documentOs={documentOs}
              viewingDuration={viewingDuration}
              onRetryAttachment={onRetryAttachment}
              retryState={retryState}
              retryError={retryError}
              transcription={transcription}
              transcriptionModel={transcriptionModel}
              transcriptionStatus={transcriptionStatus}
              transcriptionError={transcriptionError}
              onRequestTranscription={onRequestTranscription}
              transcriptionRequestState={transcriptionRequestState}
            />
            {meetingBookedFromThis && (
              <div className="mx-2 -mt-1.5 rounded-b-md rounded-t-sm bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 border border-t-0 border-blue-200 dark:border-blue-700 flex items-center justify-center gap-1 shadow-xs">
                <Time.Calendar className="size-2.5 text-blue-600 dark:text-blue-400" />
                <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300">Meeting booked</span>
                {meetingBookedFromThis.source === 'auto' ? (
                  <Editor.MagicWand01 className="size-2.5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Users.User01 className="size-2.5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
            )}
            {showTime && (
              <span
                className={cn(
                  'text-[10px] text-gray-400 dark:text-gray-500 px-1',
                  isResponse ? 'text-left' : 'text-right',
                )}
              >
                {formattedTime}
              </span>
            )}
            {isLast && showRespondIcon && (
              <Button
                size="sm"
                icon={<Arrows.ReverseLeft />}
                className="absolute bottom-0 right-2 translate-y-3/4"
                tooltip="Reply inline"
                onClick={onClickReply}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepContent (internal, pure)
// ─────────────────────────────────────────────────────────────────────────────

interface StepContentProps {
  interactionId?: string;
  dataUrl?: string | null;
  dataUrlType?: 'audio' | 'video' | 'img' | 'file' | null;
  contactName?: string;
  isResponse?: boolean;
  formattedDate?: string;
  stepType: HistoryStepType;
  action: InteractionAction;
  title?: string | null;
  body?: string | null;
  cadenceName?: string | null;
  cadenceId?: string | null;
  onClickCadence?: (cadenceId: string) => void;
  documentId?: string | null;
  documentHref?: string;
  documentTitle?: string | null;
  documentDescription?: string | null;
  documentOgImageUrl?: string | null;
  documentCity?: string | null;
  documentCountry?: string | null;
  documentBrowserName?: string | null;
  documentOs?: string | null;
  viewingDuration?: number;
  onRetryAttachment?: (interactionId: string) => void;
  retryState?: 'idle' | 'pending' | 'success' | 'error';
  retryError?: string | null;
  // Transcription data
  transcription?: string | null;
  transcriptionModel?: string | null;
  transcriptionStatus?: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | null;
  transcriptionError?: string | null;
  onRequestTranscription?: (interactionId: string) => void;
  transcriptionRequestState?: 'idle' | 'pending' | 'success' | 'error';
}

function StepContent({
  interactionId,
  dataUrl,
  dataUrlType,
  contactName,
  stepType,
  action,
  isResponse = false,
  formattedDate,
  title,
  body,
  cadenceName,
  cadenceId,
  onClickCadence,
  documentId,
  documentHref,
  documentTitle,
  documentDescription,
  documentOgImageUrl,
  documentCity,
  documentCountry,
  documentBrowserName,
  documentOs,
  viewingDuration,
  onRetryAttachment,
  retryState = 'idle',
  retryError,
  transcription,
  transcriptionModel,
  transcriptionStatus,
  transcriptionError,
  onRequestTranscription,
  transcriptionRequestState = 'idle',
}: StepContentProps) {
  const [clamped, setClamped] = React.useState(true);

  const durationMs = viewingDuration;
  const duration = durationMs != null ? formatViewDuration(durationMs) : undefined;

  const browser = documentBrowserName?.replace('iOS', '').trim();
  const location = [documentCity, documentCountry].filter(isNotNull).join(', ');
  const metaData = [browser, documentOs].filter(isNotNull).join(' ');
  const documentBodyList = [metaData, location].filter(isNotEmpty);
  const documentBody =
    documentBodyList.length > 0 ? (
      <>
        {documentBodyList.map((b, idx) => (
          <div key={`body_${idx}`}>{b}</div>
        ))}
      </>
    ) : undefined;
  const alternativeText = documentTitle || documentDescription || 'document preview';
  const processedStepType = (action ?? '').replaceAll('_', ' ').toLowerCase();

  const getContentWrapperClasses = () => {
    const baseClasses = 'overflow-hidden relative';

    if (stepType === 'error' || stepType === 'declinedInmail') {
      return cn(baseClasses, 'rounded-2xl bg-red-500');
    }

    if (isResponse) {
      return cn(
        baseClasses,
        'rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600',
      );
    } else {
      return cn(baseClasses, 'rounded-2xl rounded-tr-sm bg-slate-600 dark:bg-slate-700');
    }
  };

  const getBodyClasses = () => {
    const baseClasses = 'text-[13px] leading-[140%] tracking-[-0.01em] m-0 whitespace-pre-line wrap-break-word';
    const clampedClasses = clamped ? 'line-clamp-6 cursor-s-resize' : '';

    if (stepType === 'error' || stepType === 'declinedInmail') {
      return cn(baseClasses, clampedClasses, 'text-white');
    }

    if (isResponse) {
      return cn(baseClasses, clampedClasses, 'text-gray-900 dark:text-gray-100');
    } else {
      return cn(baseClasses, clampedClasses, 'text-white');
    }
  };

  if (stepType === 'document' && documentId) {
    return (
      <DocumentCard
        href={documentHref}
        body={documentBody}
        duration={duration}
        date={formattedDate}
        src={documentOgImageUrl ?? undefined}
        alt={alternativeText}
      />
    );
  }

  const hasBody = isNotEmpty(body);
  const onClick = () => setClamped((c) => !c);

  const bodyText = body || '';
  const hasAttachmentInBody = bodyText.includes('[Audio]') || bodyText.includes('[Attachment]');
  const hasAttachedFiles = hasAttachmentInBody || dataUrl != null || dataUrlType != null;

  const rawTitle = title || '';
  const cleanTitle = rawTitle
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
  const hasTitle = cleanTitle.length > 0;

  const isEmailType = ['mail', 'mailReplied'].includes(stepType);
  // Message/inmail bodies are stored as rich-text-editor HTML (e.g. "<p>hi <span class='mention'>Andrew</span></p>").
  // Strip the markup so the History tab shows readable text instead of raw tags. The actual LinkedIn
  // message is already plain text — Unipile strips HTML before sending — so this only fixes our display.
  const cleanBodyText = isEmailType
    ? cleanEmailContent(bodyText)
    : stripHtmlTags(bodyText).replace(/\[Audio\]/g, '').replace(/\[Attachment\]/g, '').trim();
  const hasCleanBody = cleanBodyText.length > 0;

  // Outgoing AI voice-note "transcriptions" (transcription_model === 'ai_script') are copied
  // verbatim from the step's rich-text-editor body, so they can contain raw HTML
  // (e.g. "<p>Hi Mette…</p>"). Strip the markup so the caption shows readable text instead of
  // literal tags — same treatment as cleanBodyText above. Whisper transcriptions are already
  // plain text, so this is a harmless no-op for them.
  const cleanTranscription = transcription ? stripHtmlTags(transcription) : '';

  return (
    <div
      className={cn(getContentWrapperClasses(), clamped ? 'clamped' : undefined)}
      onClick={clamped ? onClick : undefined}
    >
      {!clamped && (
        <Button
          onClick={() => setClamped((c) => !c)}
          size="sm"
          text
          icon={<General.XCircle />}
          className="absolute top-0.5 right-0.5 opacity-100 text-white dark:text-[hsl(220,13%,46%)]"
        />
      )}

      {['inmail', 'message', 'mail', 'mailReplied'].includes(stepType) && (
        <>
          {hasTitle && (
            <p className="bg-[hsla(0,0%,0%,0.3)] m-0 py-2 px-3.5 text-sm font-medium text-white">{cleanTitle}</p>
          )}
          {hasCleanBody && (
            <div className="py-2.5 px-3.5">
              <p className={getBodyClasses()}>{cleanBodyText}</p>
            </div>
          )}
          {!hasTitle && !hasCleanBody && !hasAttachedFiles && (
            <div className="py-2.5 px-3.5">
              <p className={cn(getBodyClasses(), 'italic opacity-60')}>Message content unavailable</p>
            </div>
          )}
        </>
      )}
      {stepType === 'startedCadence' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>
            Started{' '}
            {cadenceId && onClickCadence ? (
              <button
                type="button"
                onClick={() => onClickCadence(cadenceId)}
                className="font-semibold underline decoration-white/20 hover:text-blue-300 cursor-pointer">
                {cadenceName || 'Cadence'}
              </button>
            ) : (
              <span className="font-semibold underline decoration-white/20">{cadenceName || 'Cadence'}</span>
            )}
          </p>
        </div>
      )}
      {stepType === 'campaignDelete' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>
            Deleted{' '}
            {cadenceId && onClickCadence ? (
              <button
                type="button"
                onClick={() => onClickCadence(cadenceId)}
                className="font-semibold underline decoration-white/20 hover:text-blue-300 cursor-pointer">
                {cadenceName || 'Cadence'}
              </button>
            ) : (
              <span className="font-semibold underline decoration-white/20">{cadenceName || 'Cadence'}</span>
            )}
          </p>
        </div>
      )}
      {stepType === 'follow' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>Followed</p>
        </div>
      )}
      {stepType === 'comment' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>{hasBody ? body : 'Commented on post'}</p>
        </div>
      )}
      {stepType === 'error' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>{body || 'some error occurred'}</p>
        </div>
      )}
      {stepType === 'declinedInmail' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>Declined your InMail</p>
        </div>
      )}
      {stepType === 'connect' && (
        <>
          <div className="bg-[hsla(0,0%,0%,0.535)] m-0 py-2.5 px-3.5 text-white whitespace-pre-line wrap-break-word [&_p]:m-0">
            <p className={getBodyClasses()}>{getCapitalizedAllString(processedStepType)}</p>
          </div>
          {hasCleanBody && (
            <div className="py-2.5 px-3.5">
              <p className={getBodyClasses()}>{cleanBodyText}</p>
            </div>
          )}
        </>
      )}
      {stepType === 'connectAccept' && (
        <div className="py-2 px-3.5 text-center">
          <p className="text-[12px] text-gray-500 dark:text-gray-400 m-0">
            {contactName ? `${contactName} accepted your connection` : 'Connection accepted'}
          </p>
        </div>
      )}
      {stepType === 'call' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>{hasBody ? body : 'Phone Call'}</p>
        </div>
      )}
      {stepType === 'postsLike' && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>Liked posts</p>
        </div>
      )}
      {['visit', 'unknown'].includes(stepType) && (
        <div className="py-2.5 px-3.5">
          <p className={getBodyClasses()}>{getCapitalizedAllString(processedStepType)}</p>
        </div>
      )}
      {hasAttachedFiles && (
        <div className="text-[11px] leading-[14px]">
          {dataUrl != null && (
            <>
              {dataUrlType === 'audio' && <MusicPlayer src={dataUrl} />}
              {dataUrlType === 'video' && <VideoPlayer src={dataUrl} />}
              {dataUrlType === 'img' && <ImageViewer src={dataUrl} />}
              {dataUrlType === 'file' && <FileAttachment url={dataUrl} />}
              {!dataUrlType && <MusicPlayer src={dataUrl} />}
              {dataUrlType && !['audio', 'video', 'img', 'file'].includes(dataUrlType) && (
                <FileAttachment url={dataUrl} />
              )}
              {/* Transcription display for audio/video */}
              {(dataUrlType === 'audio' || dataUrlType === 'video') && (
                <div onClick={(e) => e.stopPropagation()} className="px-3 py-2">
                  {cleanTranscription && (
                    <div className="text-xs italic text-gray-300">
                      "{cleanTranscription}"
                      {transcriptionModel && transcriptionModel !== 'ai_script' && (
                        <span className="ml-1 text-[10px] opacity-60">({transcriptionModel})</span>
                      )}
                    </div>
                  )}
                  {!transcription && transcriptionStatus === 'processing' && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <span className="animate-pulse">Transcribing...</span>
                    </div>
                  )}
                  {!transcription && transcriptionStatus === 'failed' && (
                    <div className="text-xs text-red-400 dark:text-red-500 flex items-center gap-1.5">
                      <span>{transcriptionError ?? 'Transcription failed'}</span>
                      {interactionId && onRequestTranscription && (
                        <Button
                          size="xs"
                          color="red"
                          onClick={() => onRequestTranscription(interactionId)}
                          disabled={transcriptionRequestState === 'pending'}
                          tooltip="Retry transcription"
                          icon={
                            <Arrows.RefreshCcw05
                              className={cn(transcriptionRequestState === 'pending' && 'animate-spin')}
                            />
                          }
                        />
                      )}
                    </div>
                  )}
                  {!transcription &&
                    (transcriptionStatus === 'pending' || transcriptionStatus === 'skipped') &&
                    interactionId &&
                    onRequestTranscription && (
                      <Button
                        size="xs"
                        text
                        onClick={() => onRequestTranscription(interactionId)}
                        disabled={transcriptionRequestState === 'pending'}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      >
                        {transcriptionRequestState === 'pending' ? 'Requesting...' : 'Transcribe'}
                      </Button>
                    )}
                </div>
              )}
            </>
          )}
          {dataUrl == null && dataUrlType != null && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="py-2.5 px-3.5 text-sm text-gray-400 dark:text-gray-500 flex flex-col gap-1"
            >
              <div className="flex items-center gap-2">
                <span className="italic">
                  {dataUrlType === 'video' && 'Video unavailable'}
                  {dataUrlType === 'audio' && 'Audio unavailable'}
                  {dataUrlType === 'img' && 'Image unavailable'}
                  {dataUrlType === 'file' && 'File unavailable'}
                  {!['video', 'audio', 'img', 'file'].includes(dataUrlType) && 'Attachment unavailable'}
                </span>
                {interactionId && onRetryAttachment && (
                  <Button
                    size="xs"
                    color={retryState === 'error' ? 'red' : undefined}
                    onClick={() => onRetryAttachment(interactionId)}
                    disabled={retryState === 'pending'}
                    tooltip="Retry downloading attachment"
                    icon={<Arrows.RefreshCcw05 className={cn(retryState === 'pending' && 'animate-spin')} />}
                  />
                )}
              </div>
              {retryState === 'error' && (
                <span className="text-xs text-red-500 dark:text-red-400">
                  {retryError || 'Retry failed. Try again.'}
                </span>
              )}
            </div>
          )}
          {dataUrl == null && dataUrlType == null && hasAttachmentInBody && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="py-2.5 px-3.5 text-sm text-gray-400 dark:text-gray-500 flex flex-col gap-1"
            >
              <div className="flex items-center gap-2">
                <span className="italic">Attachment unavailable</span>
                {interactionId && onRetryAttachment && (
                  <Button
                    size="xs"
                    color={retryState === 'error' ? 'red' : undefined}
                    onClick={() => onRetryAttachment(interactionId)}
                    disabled={retryState === 'pending'}
                    tooltip="Retry downloading attachment"
                    icon={<Arrows.RefreshCcw05 className={cn(retryState === 'pending' && 'animate-spin')} />}
                  />
                )}
              </div>
              {retryState === 'error' && (
                <span className="text-xs text-red-500 dark:text-red-400">
                  {retryError || 'Retry failed. Try again.'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
